import { ALL_FACTORS, FACTORS, TAG_KINDS, pretty, scorePair } from "./graphModel";
import { recommendClusters } from "./userClusters";

const pct = (v) => Math.round(v * 100);

// Tags that apply a filter when clicked; `counts` optionally shows how many games carry each.
const TagButtons = ({ kind, items, format, counts, onTag, empty }) => (
  <div className="ludograph-tags">
    {items.map((t) => (
      <button
        key={t}
        className="tag tag-neutral ludograph-tag-btn"
        onClick={() => onTag(kind, t)}
        title={`Show games with ${format(t)}`}
      >
        {format(t)}
        {counts && <span className="ludograph-num ludograph-tag-count">{counts[t]}</span>}
      </button>
    ))}
    {!items.length && empty && <span className="ludograph-small-text">{empty}</span>}
  </div>
);

const Tags = ({ items, variant = "neutral", empty }) => (
  <div className="ludograph-tags">
    {items.map((t) => (
      <span key={t} className={`tag tag-${variant}`}>
        {t}
      </span>
    ))}
    {!items.length && empty && <span className="ludograph-small-text">{empty}</span>}
  </div>
);

const ContributionBar = ({ contrib, height = 6 }) => (
  <span className="ludograph-bar" style={{ height }}>
    {ALL_FACTORS.map((f) => (
      <span key={f.key} style={{ width: `${contrib[f.key] * 100}%`, background: f.color }} />
    ))}
  </span>
);

const Legend = () => (
  <div className="ludograph-legend">
    {ALL_FACTORS.map((f) => (
      <span key={f.key}>
        <span className="ludograph-swatch" style={{ background: f.color }} />
        {f.label}
      </span>
    ))}
  </div>
);

// Games ranked by total link weight, with a bar relative to the whole graph.
const StrengthList = ({ indices, graph, games, onSelect, onEnter, onLeave }) => {
  const maxStrength = Math.max(...graph.strength) || 1;
  return indices.map((i) => (
    <button
      key={games[i].id}
      className="ludograph-top"
      onClick={() => onSelect(i)}
      onMouseEnter={() => onEnter(i)}
      onMouseLeave={onLeave}
    >
      <span className="ludograph-top-name">{games[i].name}</span>
      <span className="ludograph-num ludograph-small-text">{graph.adj[i].length} links</span>
      <span className="ludograph-bar ludograph-top-bar">
        <span
          style={{
            width: `${(graph.strength[i] / maxStrength) * 100}%`,
            background: "var(--color-text)",
          }}
        />
      </span>
    </button>
  ));
};

const EmptyView = ({ hidden, ...props }) => (
  <>
    <section className="ludograph-section">
      <h3>Select a game</h3>
      <p className="ludograph-muted">
        Click any square to see what links it to the rest of your shelf, or pick a cluster on the
        left.
      </p>
    </section>
    <section className="ludograph-section ludograph-section-last">
      <h6>Most connected</h6>
      <StrengthList
        indices={props.graph.topList.filter((i) => !hidden || !hidden.has(i)).slice(0, 8)}
        {...props}
      />
    </section>
  </>
);

const ClusterView = ({ cluster, onCloseCluster, userClusters, onCreateUserCluster, ...props }) => {
  const { graph } = props;
  const members = [...cluster.members].sort((a, b) => graph.strength[b] - graph.strength[a]);
  const free = members.filter((i) => userClusters.clusterOf[i] == null);
  const taken = members.length - free.length;
  return (
    <>
      <section className="ludograph-section">
        <div className="card-kicker">Suggested cluster</div>
        <h2 className="ludograph-title">{cluster.name}</h2>
        <div className="ludograph-body-text">{cluster.size} games</div>
        {taken > 0 && (
          <div className="ludograph-muted ludograph-small">
            {taken} already in your clusters{free.length ? " and will stay there" : ""}.
          </div>
        )}
        <div className="ludograph-actions">
          <button
            className="btn btn-primary"
            disabled={!free.length}
            onClick={() => onCreateUserCluster(cluster.name, free)}
          >
            Save as my cluster
          </button>
          <button className="btn btn-secondary" onClick={onCloseCluster}>
            Close
          </button>
        </div>
      </section>
      <section className="ludograph-section ludograph-section-last">
        <h6>Games in this cluster</h6>
        <StrengthList indices={members} {...props} />
      </section>
    </>
  );
};

// Evidence that a cluster's games belong together, for checking it by eye.
const ClusterStats = ({ stats, games, onSelect, onEnter, onLeave, onStartTagFilter }) => {
  const { cohesion, baseline, lift, linked, pairCount, share, fits, sharedTags } = stats;
  const anyShared = sharedTags.some((t) => t.items.length);
  return (
    <>
      <div className="ludograph-stat-pair">
        <div>
          <div className="ludograph-stat-value ludograph-stat-lg">{pct(cohesion)}%</div>
          <div className="ludograph-stat-label">Avg link inside</div>
        </div>
        <div>
          <div className="ludograph-stat-value ludograph-stat-lg">{lift.toFixed(1)}×</div>
          <div className="ludograph-stat-label">vs collection ({pct(baseline)}%)</div>
        </div>
      </div>
      <div className="ludograph-stat-pair">
        <div>
          <div className="ludograph-stat-value ludograph-stat-lg">
            {linked}/{pairCount}
          </div>
          <div className="ludograph-stat-label">Pairs linked</div>
        </div>
        <div>
          <div className="ludograph-stat-value ludograph-stat-lg">{pct(fits[0].fit)}%</div>
          <div className="ludograph-stat-label">Weakest fit</div>
        </div>
      </div>
      <section className="ludograph-section">
        <h6>What links them</h6>
        <p className="ludograph-muted ludograph-small">Share of the average link inside the cluster from each factor.</p>
        <Legend />
        <ContributionBar contrib={share} height={10} />
      </section>
      <section className="ludograph-section">
        <h6>Weakest fits</h6>
        <p className="ludograph-muted ludograph-small">
          Each game's average link to the rest of the cluster. Games below the collection average of {pct(baseline)}%
          probably belong elsewhere.
        </p>
        {fits.slice(0, 3).map(({ i, fit }) => (
          <div
            key={games[i].id}
            className="ludograph-conn"
            onClick={() => onSelect(i)}
            onMouseEnter={() => onEnter(i)}
            onMouseLeave={onLeave}
          >
            <span className="ludograph-conn-name">{games[i].name}</span>
            <span className="ludograph-num ludograph-conn-pct">{pct(fit)}%</span>
            <span className="ludograph-small-text">{fit < baseline ? "Below average" : ""}</span>
          </div>
        ))}
      </section>
      <section className="ludograph-section ludograph-section-last ludograph-stack">
        <p className="ludograph-muted ludograph-small">
          Tags at least half the games share. The number is how many of the {fits.length} carry it.
        </p>
        {sharedTags.map(({ kind, label, format, counts, items }) => (
          <div key={kind}>
            <h6>Shared {label.toLowerCase()}</h6>
            <TagButtons kind={kind} items={items} format={format} counts={counts} onTag={onStartTagFilter} empty="None" />
          </div>
        ))}
        {!anyShared && <p className="ludograph-body-text ludograph-muted">No tag is shared by half the cluster.</p>}
      </section>
    </>
  );
};

const UserClusterView = ({
  userCluster,
  suggestions,
  userClusterStats,
  suggestFromOthers,
  onSuggestFromOthers,
  userClusters,
  onStartTagFilter,
  games,
  onSelect,
  onEnter,
  onLeave,
  onAssignUserCluster,
  onRenameUserCluster,
  onDeleteUserCluster,
  onCloseUserCluster,
}) => (
  <>
    <section className="ludograph-section">
      <div className="card-kicker">Your cluster</div>
      <input
        className="input ludograph-name-input"
        value={userCluster.name}
        onChange={(e) => onRenameUserCluster(userCluster.id, e.target.value)}
        aria-label="Cluster name"
      />
      <div className="ludograph-body-text">
        {userCluster.size} {userCluster.size === 1 ? "game" : "games"}
      </div>
      <div className="ludograph-actions">
        <button className="btn btn-secondary" onClick={onCloseUserCluster}>
          Close
        </button>
        <button className="btn btn-ghost" onClick={() => onDeleteUserCluster(userCluster.id)}>
          Delete cluster
        </button>
      </div>
    </section>
    <section className="ludograph-section">
      <h6>Games in this cluster</h6>
      {userCluster.members.map((i) => (
        <div
          key={games[i].id}
          className="ludograph-conn"
          onClick={() => onSelect(i)}
          onMouseEnter={() => onEnter(i)}
          onMouseLeave={onLeave}
        >
          <span className="ludograph-conn-name">{games[i].name}</span>
          <span />
          <button
            className="btn btn-ghost ludograph-conn-cmp"
            onClick={(ev) => {
              ev.stopPropagation();
              onAssignUserCluster(i, null);
            }}
          >
            Remove
          </button>
        </div>
      ))}
    </section>
    <section className={`ludograph-section${userClusterStats ? "" : " ludograph-section-last"}`}>
      <h6>Suggestions</h6>
      <p className="ludograph-muted ludograph-small">
        {suggestFromOthers ? "Games outside this cluster" : "Unclustered games"} with the strongest average link to
        every game in this cluster.
      </p>
      <label className="ludograph-toggle">
        <input type="checkbox" checked={suggestFromOthers} onChange={(e) => onSuggestFromOthers(e.target.checked)} />
        <span>Include games in other clusters</span>
      </label>
      {suggestions.map(({ i, s, closest, from }) => (
        <div
          key={games[i].id}
          className="ludograph-conn"
          onClick={() => onSelect(i)}
          onMouseEnter={() => onEnter(i)}
          onMouseLeave={onLeave}
        >
          <span className="ludograph-conn-name">{games[i].name}</span>
          <span className="ludograph-num ludograph-conn-pct">{pct(s)}%</span>
          <button
            className="btn btn-ghost ludograph-conn-cmp"
            onClick={(ev) => {
              ev.stopPropagation();
              onAssignUserCluster(i, userCluster.id);
            }}
            title={from ? `Move out of ${userClusters.byId[from].name}` : undefined}
          >
            {from ? "Move" : "Add"}
          </button>
          <span className="ludograph-conn-sub">
            {from && `In ${userClusters.byId[from].name} · `}Closest to {games[closest].name}
          </span>
        </div>
      ))}
      {!suggestions.length && (
        <p className="ludograph-body-text ludograph-muted">
          {!userCluster.size
            ? "Add a game to get suggestions."
            : suggestFromOthers
              ? "Every game is already in this cluster."
              : "Every game is already in a cluster."}
        </p>
      )}
    </section>
    {userClusterStats && (
      <ClusterStats
        stats={userClusterStats}
        games={games}
        onSelect={onSelect}
        onEnter={onEnter}
        onLeave={onLeave}
        onStartTagFilter={onStartTagFilter}
      />
    )}
  </>
);

const NEW_CLUSTER = "__new__";

// Lets a game join, move between or leave user clusters.
const UserClusterPicker = ({ game, index, userClusters, onAssignUserCluster, onCreateUserCluster, onOpenUserCluster }) => {
  const current = userClusters.clusterOf[index];
  return (
    <div className="ludograph-user-assign">
      <select
        className="input"
        value={current || ""}
        aria-label="Your cluster"
        onChange={(e) => {
          const v = e.target.value;
          if (v === NEW_CLUSTER) onCreateUserCluster(`${game.name} & co.`, [index]);
          else onAssignUserCluster(index, v || null);
        }}
      >
        <option value="">Not in your clusters</option>
        {userClusters.list.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
        <option value={NEW_CLUSTER}>New cluster…</option>
      </select>
      {current && (
        <button className="btn btn-ghost ludograph-nowrap" onClick={() => onOpenUserCluster(current)}>
          Open
        </button>
      )}
    </div>
  );
};

// Games carrying every tag in `tagFilter`, plus the tags they share for narrowing further.
const TagFilterView = ({
  tagFilter,
  tagMatches,
  userClusters,
  onAddTagFilter,
  onRemoveTagFilter,
  onClearTagFilter,
  onCreateUserCluster,
  ...props
}) => {
  const { graph, games } = props;
  const members = [...tagMatches].sort((a, b) => graph.strength[b] - graph.strength[a]);
  const free = members.filter((i) => userClusters.clusterOf[i] == null);
  const formatOf = Object.fromEntries(TAG_KINDS.map((k) => [k.kind, k.format]));
  const name = tagFilter.map(({ kind, value }) => formatOf[kind](value)).join(" · ");
  const common = TAG_KINDS.map(({ kind, label, format }) => {
    const counts = {};
    members.forEach((i) =>
      games[i][kind].forEach((t) => {
        counts[t] = (counts[t] || 0) + 1;
      })
    );
    const active = new Set(tagFilter.filter((t) => t.kind === kind).map((t) => t.value));
    const items = Object.keys(counts)
      .filter((t) => !active.has(t) && (counts[t] > 1 || members.length < 3))
      .sort((a, b) => counts[b] - counts[a] || a.localeCompare(b))
      .slice(0, 12);
    return { kind, label, format, counts, items };
  });
  return (
    <>
      <section className="ludograph-section">
        <div className="card-kicker">Filter</div>
        <div className="ludograph-tags ludograph-filter-tags">
          {tagFilter.map(({ kind, value }) => (
            <button
              key={`${kind}:${value}`}
              className="tag tag-accent ludograph-tag-btn"
              onClick={() => onRemoveTagFilter(kind, value)}
              title="Remove filter"
            >
              {formatOf[kind](value)}
              <span aria-hidden="true">×</span>
            </button>
          ))}
        </div>
        <div className="ludograph-body-text">
          {members.length} {members.length === 1 ? "game" : "games"}
        </div>
        <div className="ludograph-actions">
          <button
            className="btn btn-primary"
            disabled={!free.length}
            onClick={() => onCreateUserCluster(name, free)}
          >
            Save as my cluster
          </button>
          <button className="btn btn-secondary" onClick={onClearTagFilter}>
            Close
          </button>
        </div>
      </section>
      {members.length > 1 && (
        <section className="ludograph-section ludograph-stack">
          <p className="ludograph-muted ludograph-small">Common among these games. Click to narrow down.</p>
          {common.map(({ kind, label, format, counts, items }) => (
            <div key={kind}>
              <h6>{label}</h6>
              <TagButtons kind={kind} items={items} format={format} counts={counts} onTag={onAddTagFilter} empty="None" />
            </div>
          ))}
        </section>
      )}
      <section className="ludograph-section ludograph-section-last">
        <h6>Matching games</h6>
        <StrengthList indices={members} {...props} />
        {!members.length && <p className="ludograph-body-text ludograph-muted">No games match every tag.</p>}
      </section>
    </>
  );
};

const RecommendedClusters = ({
  model,
  graph,
  index,
  userClusters,
  weights,
  seriesWeight,
  onOpenUserCluster,
  onAssignUserCluster,
  last,
}) => {
  const recs = recommendClusters(model, graph, userClusters, index, weights, seriesWeight);
  if (!recs.length) return null;
  const inCluster = userClusters.clusterOf[index] != null;
  return (
    <section className={`ludograph-section${last ? " ludograph-section-last" : ""}`}>
      <h6>Recommended clusters</h6>
      <p className="ludograph-muted ludograph-small">
        Your clusters this game links to. The percentage is its average link to every member.
      </p>
      {recs.map(({ cluster, links, closest, fit }) => (
        <div key={cluster.id} className="ludograph-conn" onClick={() => onOpenUserCluster(cluster.id)}>
          <span className="ludograph-conn-name">{cluster.name}</span>
          <span className="ludograph-num ludograph-conn-pct">{pct(fit)}%</span>
          <button
            className="btn btn-ghost ludograph-conn-cmp"
            onClick={(ev) => {
              ev.stopPropagation();
              onAssignUserCluster(index, cluster.id);
            }}
          >
            {inCluster ? "Move" : "Add"}
          </button>
          <span className="ludograph-conn-sub">
            Linked to {links} of {cluster.size}
            {links === 1 ? ` (${model.games[closest].name})` : ` · closest ${model.games[closest].name}`}
          </span>
        </div>
      ))}
    </section>
  );
};

const GameView = ({
  game,
  index,
  graph,
  games,
  onSelect,
  onEnter,
  onLeave,
  onCompare,
  onStartPick,
  onClose,
  onStartTagFilter,
  ...props
}) => {
  const conns = graph.adj[index];
  const inCluster = props.userClusters.clusterOf[index] != null;
  return (
    <>
      <section className="ludograph-section">
        <div className="card-kicker">
          {game.year || "—"} · {game.publisher}
        </div>
        <h2 className="ludograph-title">{game.name}</h2>
        <div className="ludograph-body-text">
          {game.designers.length ? game.designers.join(", ") : "Designer unknown"}
        </div>
        <div className="ludograph-muted ludograph-small">
          Difficulty {game.complexity ? `${game.complexity.toFixed(1)} / 5` : "unrated"}
        </div>
        {(game.series || game.franchise) && (
          <div className="ludograph-muted ludograph-small">
            {[game.series && `${game.series} series`, game.franchise].filter(Boolean).join(" · ")}
          </div>
        )}
        <div className="ludograph-actions">
          <button className="btn btn-primary ludograph-nowrap" onClick={onStartPick}>
            Compare with…
          </button>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
        <UserClusterPicker game={game} index={index} {...props} />
      </section>
      <div className="ludograph-stat-pair">
        <div>
          <div className="ludograph-stat-value ludograph-stat-lg">{conns.length}</div>
          <div className="ludograph-stat-label">Links</div>
        </div>
        <div>
          <div className="ludograph-stat-value ludograph-stat-lg">{graph.strength[index].toFixed(1)}</div>
          <div className="ludograph-stat-label">Total weight</div>
        </div>
      </div>
      <section className="ludograph-section ludograph-stack">
        {TAG_KINDS.map(({ kind, label, format }) => (
          <div key={kind}>
            <h6>{label}</h6>
            <TagButtons kind={kind} items={game[kind]} format={format} onTag={onStartTagFilter} empty="None listed" />
          </div>
        ))}
      </section>
      {!inCluster && <RecommendedClusters graph={graph} index={index} {...props} />}
      <section className={`ludograph-section${inCluster ? "" : " ludograph-section-last"}`}>
        <h6>Strongest connections</h6>
        <Legend />
        {conns.map(({ o, e }) => (
          <div
            key={games[o].id}
            className="ludograph-conn"
            onClick={() => onSelect(o)}
            onMouseEnter={() => onEnter(o)}
            onMouseLeave={onLeave}
          >
            <span className="ludograph-conn-name">{games[o].name}</span>
            <span className="ludograph-num ludograph-conn-pct">{pct(e.s)}%</span>
            <button
              className="btn btn-ghost ludograph-conn-cmp"
              onClick={(ev) => {
                ev.stopPropagation();
                onCompare(o);
              }}
            >
              Compare
            </button>
            <ContributionBar contrib={e.contrib} />
          </div>
        ))}
        {!conns.length && (
          <p className="ludograph-body-text ludograph-muted">
            No links at this threshold. Lower the minimum strength or shift the weighting.
          </p>
        )}
      </section>
      {inCluster && <RecommendedClusters graph={graph} index={index} {...props} last />}
    </>
  );
};

const CompareView = ({ model, graph, weights, shares, seriesWeight, sel, cmp, path, onSelect, onSwap, onBack, onClose }) => {
  const { games, pairIndex, n } = model;
  const A = games[sel];
  const B = games[cmp];
  const p = pairIndex[sel * n + cmp];
  const { s, contrib } = scorePair(p, weights, seriesWeight);
  const linked = graph.edges.some((e) => e.a === p.a && e.b === p.b);
  const shared = (a, b) => a.filter((x) => b.includes(x));
  const only = (a, b) => a.filter((x) => !b.includes(x));
  const sharedDesigners = shared(A.designers, B.designers);
  let designerText = "No shared designer or publisher";
  if (sharedDesigners.length) designerText = `Both designed by ${sharedDesigners.join(", ")}`;
  else if (A.publisher === B.publisher) designerText = `Different designers, both published by ${A.publisher}`;
  else if (A.publisherGroup && A.publisherGroup === B.publisherGroup)
    designerText = `${A.publisher} and ${B.publisher} are both part of ${A.publisherGroup}`;

  const rows = [
    {
      label: "Same series",
      sim: p.series ? "Yes" : "No",
      wt: seriesWeight ? `${seriesWeight}% floor` : "Off",
      add: `+${pct(contrib.series)}`,
    },
    ...FACTORS.map((f) => ({
      label: f.label,
      sim: `${pct(p.c[f.key])}%`,
      wt: `${shares[f.key]}%`,
      add: `+${pct(contrib[f.key])}`,
    })),
  ];

  return (
    <>
      <section className="ludograph-section">
        <div className="card-kicker">Comparison</div>
        <div className="ludograph-cmp-names">
          <button className="ludograph-cmp-name ludograph-unbutton" onClick={onBack}>
            {A.name}
          </button>
          <span className="ludograph-muted ludograph-small">and</span>
          <span className="ludograph-cmp-name">{B.name}</span>
        </div>
        <div className="ludograph-big-pct">{pct(s)}%</div>
        <div className="ludograph-cmp-sub">
          link strength · {linked ? "linked directly" : "not linked at current settings"}
        </div>
        <div className="ludograph-actions">
          <button className="btn btn-secondary" onClick={onSwap}>
            Swap
          </button>
          <button className="btn btn-secondary" onClick={onBack}>
            Back
          </button>
          <button className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </section>
      <section className="ludograph-section ludograph-table-section">
        <table className="table">
          <thead>
            <tr>
              <th>Factor</th>
              <th className="ludograph-align-right">Overlap</th>
              <th className="ludograph-align-right">Weight</th>
              <th className="ludograph-align-right">Adds</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td className="ludograph-strong">{r.label}</td>
                <td className="ludograph-align-right ludograph-num">{r.sim}</td>
                <td className="ludograph-align-right ludograph-num">{r.wt}</td>
                <td className="ludograph-align-right ludograph-num ludograph-bold">{r.add}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section className="ludograph-section ludograph-stack">
        <div>
          <h6>Shared themes</h6>
          <Tags items={shared(A.themes, B.themes).map(pretty)} variant="accent" empty="None" />
        </div>
        <div>
          <h6>Shared genres</h6>
          <Tags items={shared(A.genres, B.genres).map(pretty)} variant="accent" empty="None" />
        </div>
        <div>
          <h6>Shared mechanics</h6>
          <Tags items={shared(A.mechanics, B.mechanics)} variant="accent" empty="None" />
        </div>
        <div className="ludograph-two-col">
          <div>
            <div className="ludograph-muted ludograph-small ludograph-label">Only {A.name}</div>
            <Tags items={only(A.mechanics, B.mechanics)} />
          </div>
          <div>
            <div className="ludograph-muted ludograph-small ludograph-label">Only {B.name}</div>
            <Tags items={only(B.mechanics, A.mechanics)} />
          </div>
        </div>
        <div>
          <h6>Designer / publisher</h6>
          <div className="ludograph-body-text">{designerText}</div>
        </div>
      </section>
      <section className="ludograph-section ludograph-section-last">
        <h6>Path through your collection</h6>
        <p className="ludograph-muted ludograph-small">The strongest chain of links between the two games.</p>
        {path ? (
          path.map((i, idx) => {
            const last = idx === path.length - 1;
            const next = last
              ? null
              : scorePair(pairIndex[i * n + path[idx + 1]], weights, seriesWeight).s;
            return (
              <div key={games[i].id} className="ludograph-path-step">
                <div className="ludograph-path-rail">
                  <span
                    className="ludograph-path-dot"
                    style={{
                      background: idx === 0 || last ? "var(--color-accent)" : "var(--color-text)",
                    }}
                  />
                  <span
                    className="ludograph-path-line"
                    style={{ background: last ? "transparent" : "var(--color-accent)" }}
                  />
                </div>
                <button className="ludograph-path-name ludograph-unbutton" onClick={() => onSelect(i)}>
                  {games[i].name}
                </button>
                <span className="ludograph-num ludograph-muted ludograph-small">
                  {last ? "" : `${pct(next)}%`}
                </span>
              </div>
            );
          })
        ) : (
          <p className="ludograph-body-text">No path at this threshold.</p>
        )}
      </section>
    </>
  );
};

const HistoryNav = ({ canBack, canForward, onHistoryBack, onHistoryForward }) => (
  <div className="ludograph-detail-nav">
    <button className="btn btn-icon" onClick={onHistoryBack} disabled={!canBack} title="Back" aria-label="Back">
      <svg width="14" height="14" viewBox="0 0 24 24" className="ludograph-icon">
        <path d="M15 18l-6-6 6-6" />
      </svg>
    </button>
    <button className="btn btn-icon" onClick={onHistoryForward} disabled={!canForward} title="Forward" aria-label="Forward">
      <svg width="14" height="14" viewBox="0 0 24 24" className="ludograph-icon">
        <path d="M9 18l6-6-6-6" />
      </svg>
    </button>
  </div>
);

const DetailPanel = ({ className, style, ...props }) => {
  const { model, graph, sel, cmp, cluster, userCluster, tagMatches } = props;
  const activeFilter = sel == null && tagMatches != null;
  const activeCluster = sel == null && !activeFilter && cluster != null ? graph.clusters[cluster] : null;
  const activeUserCluster = sel == null && !activeFilter && !activeCluster ? userCluster : null;
  return (
    <aside className={className} style={style}>
      {(props.canBack || props.canForward) && <HistoryNav {...props} />}
      {sel == null && !activeFilter && !activeCluster && !activeUserCluster && (
        <EmptyView games={model.games} {...props} />
      )}
      {activeFilter && <TagFilterView games={model.games} {...props} />}
      {activeCluster && <ClusterView games={model.games} {...props} cluster={activeCluster} />}
      {activeUserCluster && <UserClusterView games={model.games} {...props} userCluster={activeUserCluster} />}
      {sel != null && cmp == null && (
        <GameView game={model.games[sel]} index={sel} games={model.games} {...props} />
      )}
      {sel != null && cmp != null && <CompareView {...props} />}
    </aside>
  );
};

export default DetailPanel;
